import React, { Component } from "react";
import {
  Alert,
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid as Container,
  Row,
  Table
} from "react-bootstrap";

import Card from "components/Card/Card.jsx";
import CollectionStatisticsChart from "components/Charts/CollectionStatisticsChart";
import ViewportAlert from "components/ViewportAlert/ViewportAlert";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://hgsystem.pl/mangusta";

function dateInputValue(date) {
  const pad = value => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

function presetDates(preset) {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start;
  if (preset === "current_month")
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  else if (preset === "previous_month") {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end.setTime(new Date(now.getFullYear(), now.getMonth(), 0).getTime());
  } else {
    const months = preset === "3m" ? 3 : preset === "6m" ? 6 : 12;
    start = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
  }
  return { from: dateInputValue(start), to: dateInputValue(end) };
}

class CollectionStatistics extends Component {
  constructor(props) {
    super(props);
    const dates = presetDates("12m");
    this.state = {
      preset: "12m",
      from: dates.from,
      to: dates.to,
      deviceId: "",
      loading: false,
      exporting: false,
      error: "",
      summary: {},
      monthly: [],
      collections: [],
      truncated: false
    };
  }

  componentDidMount() {
    this.loadStatistics();
  }

  post = body => {
    if (!this.props.credentials)
      return Promise.reject(new Error("Brak danych uwierzytelniających."));
    return fetch(`${API_BASE}/collection_statistics.php`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        auth: this.props.credentials,
        ...body
      })
    }).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data.error || "Nie udało się pobrać statystyk.");
      return data;
    });
  };

  changePreset = event => {
    const preset = event.target.value;
    if (preset === "custom") {
      this.setState({ preset });
      return;
    }
    const dates = presetDates(preset);
    this.setState({ preset, ...dates }, this.loadStatistics);
  };

  fillMonthlyPeriods = monthly => {
    const source = Array.isArray(monthly) ? monthly : [];
    const byMonth = {};
    source.forEach(item => {
      byMonth[item.month] = item;
    });
    const start = new Date(`${this.state.from.slice(0, 7)}-01T00:00:00`);
    const end = new Date(`${this.state.to.slice(0, 7)}-01T00:00:00`);
    const result = [];
    const cursor = new Date(start.getTime());
    while (cursor <= end && result.length < 240) {
      const month = dateInputValue(cursor).slice(0, 7);
      result.push(
        byMonth[month] || {
          month,
          collection_count: 0,
          total_amount: 0,
          average_amount: null,
          average_temperature: null
        }
      );
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return result;
  };

  loadStatistics = () => {
    const fromDate = new Date(`${this.state.from}T00:00:00`);
    const toDate = new Date(`${this.state.to}T23:59:59`);
    if (
      Number.isNaN(fromDate.getTime()) ||
      Number.isNaN(toDate.getTime()) ||
      toDate < fromDate
    ) {
      this.setState({ error: "Wybierz prawidłowy zakres dat." });
      return;
    }

    this.setState({ loading: true, error: "" });
    this.post({
      from: Math.floor(fromDate.getTime() / 1000),
      to: Math.min(
        Math.floor(toDate.getTime() / 1000),
        Math.floor(Date.now() / 1000) + 21600
      ),
      device_id: this.state.deviceId
    })
      .then(data =>
        this.setState({
          loading: false,
          summary: data.summary || {},
          monthly: this.fillMonthlyPeriods(data.monthly),
          collections: Array.isArray(data.collections)
            ? data.collections
            : [],
          truncated: data.collections_truncated === true
        })
      )
      .catch(error =>
        this.setState({ loading: false, error: error.message })
      );
  };

  exportPdf = () => {
    if (!this.statisticsChart) return;
    this.setState({ exporting: true });
    this.statisticsChart
      .exportPdf()
      .catch(error => this.setState({ error: error.message }))
      .then(() => this.setState({ exporting: false }));
  };

  formatNumber = (value, digits) => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    if (!Number.isFinite(number)) return "—";
    return number.toLocaleString("pl-PL", {
      minimumFractionDigits: digits || 0,
      maximumFractionDigits: digits || 0
    });
  };

  formatDate = timestamp =>
    new Date(Number(timestamp) * 1000).toLocaleString("pl-PL");

  formatMonth = month => {
    const date = new Date(`${month}-01T00:00:00`);
    return Number.isNaN(date.getTime())
      ? month
      : date.toLocaleDateString("pl-PL", {
          year: "numeric",
          month: "long"
        });
  };

  renderMetric(title, value, category) {
    return (
      <Col lg={3} sm={6}>
        <Card
          title={value}
          category={title}
          content={<div className="text-muted">{category}</div>}
        />
      </Col>
    );
  }

  render() {
    const devices = (Array.isArray(this.props.device)
      ? this.props.device
      : []
    ).filter(
      (device, index, allDevices) =>
        allDevices.findIndex(
          item => String(item.id) === String(device.id)
        ) === index
    );
    const summary = this.state.summary;
    const selectedDevice = devices.find(
      device => String(device.id) === this.state.deviceId
    );
    const reportTitle = `Statystyki odbiorów — ${
      selectedDevice ? selectedDevice.desc || selectedDevice.id : "wszystkie urządzenia"
    } — ${this.state.from}–${this.state.to}`;

    return (
      <div className="content">
        <Container fluid>
          <Row>
            <Col md={12}>
              <Card
                title="Statystyki odbiorów"
                category="Miesięczne podsumowanie odebranego surowca"
                content={
                  <Row>
                    <Col sm={6} md={3}>
                      <FormGroup>
                        <ControlLabel>Urządzenie</ControlLabel>
                        <FormControl
                          componentClass="select"
                          value={this.state.deviceId}
                          disabled={this.state.loading}
                          onChange={event =>
                            this.setState(
                              { deviceId: event.target.value },
                              this.loadStatistics
                            )
                          }
                        >
                          <option value="">Wszystkie urządzenia</option>
                          {devices.map(device => (
                            <option value={device.id} key={device.id}>
                              {device.desc || device.id}
                            </option>
                          ))}
                        </FormControl>
                      </FormGroup>
                    </Col>
                    <Col sm={6} md={3}>
                      <FormGroup>
                        <ControlLabel>Gotowy okres</ControlLabel>
                        <FormControl
                          componentClass="select"
                          value={this.state.preset}
                          onChange={this.changePreset}
                          disabled={this.state.loading}
                        >
                          <option value="current_month">Bieżący miesiąc</option>
                          <option value="previous_month">Poprzedni miesiąc</option>
                          <option value="3m">Ostatnie 3 miesiące</option>
                          <option value="6m">Ostatnie 6 miesięcy</option>
                          <option value="12m">Ostatnie 12 miesięcy</option>
                          <option value="custom">Własny zakres</option>
                        </FormControl>
                      </FormGroup>
                    </Col>
                    <Col sm={6} md={2}>
                      <FormGroup>
                        <ControlLabel>Od</ControlLabel>
                        <FormControl
                          type="date"
                          value={this.state.from}
                          onChange={event =>
                            this.setState({
                              from: event.target.value,
                              preset: "custom"
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={6} md={2}>
                      <FormGroup>
                        <ControlLabel>Do</ControlLabel>
                        <FormControl
                          type="date"
                          value={this.state.to}
                          onChange={event =>
                            this.setState({
                              to: event.target.value,
                              preset: "custom"
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={6} md={2}>
                      <Button
                        bsStyle="info"
                        block
                        style={{ marginTop: 25 }}
                        disabled={this.state.loading}
                        onClick={this.loadStatistics}
                      >
                        {this.state.loading ? "Pobieranie…" : "Pokaż"}
                      </Button>
                    </Col>
                  </Row>
                }
              />
            </Col>
          </Row>

          {this.state.error && (
          <ViewportAlert
            bsStyle="danger"
            onDismiss={() => this.setState({ error: "" })}
          >
            {this.state.error}
          </ViewportAlert>
          )}
          {this.state.truncated && (
            <Row>
              <Col md={12}>
                <Alert bsStyle="warning">
                  Tabela szczegółowa pokazuje pierwsze 5000 odbiorów. Podsumowania
                  miesięczne obejmują cały wybrany okres.
                </Alert>
              </Col>
            </Row>
          )}

          <Row>
            {this.renderMetric(
              "Łączna ilość",
              this.formatNumber(summary.total_amount, 1),
              "w wybranym okresie"
            )}
            {this.renderMetric(
              "Liczba odbiorów",
              this.formatNumber(summary.collection_count),
              `${this.formatNumber(summary.device_count)} urządzeń`
            )}
            {this.renderMetric(
              "Średni odbiór",
              this.formatNumber(summary.average_amount, 1),
              "ilość na jeden odbiór"
            )}
            {this.renderMetric(
              "Średnia temperatura",
              summary.average_temperature === null ||
              summary.average_temperature === undefined
                ? "—"
                : `${this.formatNumber(summary.average_temperature, 1)} °C`,
              "dla odbiorów z temperaturą"
            )}
          </Row>

          <Row>
            <Col md={12}>
              <Card
                title="Odbiory według miesięcy"
                category={reportTitle}
                content={
                  <div>
                    <div className="text-right" style={{ marginBottom: 10 }}>
                      <Button
                        bsStyle="default"
                        onClick={this.exportPdf}
                        disabled={
                          this.state.exporting ||
                          this.state.loading ||
                          this.state.monthly.length === 0
                        }
                      >
                        {this.state.exporting
                          ? "Tworzenie PDF…"
                          : "Pobierz wykres i tabele PDF"}
                      </Button>
                    </div>
                    <CollectionStatisticsChart
                      ref={component => {
                        this.statisticsChart = component;
                      }}
                      monthly={this.state.monthly}
                      collections={this.state.collections}
                      title={reportTitle}
                      fileName={`statystyki-odbiorow-${this.state.from}-${this.state.to}`}
                    />
                  </div>
                }
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card
                title="Podsumowanie miesięczne"
                ctTableFullWidth
                ctTableResponsive
                content={
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Miesiąc</th>
                        <th>Liczba odbiorów</th>
                        <th>Łączna ilość</th>
                        <th>Średni odbiór</th>
                        <th>Średnia temperatura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.monthly.map(item => (
                        <tr key={item.month}>
                          <td>{this.formatMonth(item.month)}</td>
                          <td>{this.formatNumber(item.collection_count)}</td>
                          <td>{this.formatNumber(item.total_amount, 1)}</td>
                          <td>{this.formatNumber(item.average_amount, 1)}</td>
                          <td>
                            {item.average_temperature === null
                              ? "—"
                              : `${this.formatNumber(
                                  item.average_temperature,
                                  1
                                )} °C`}
                          </td>
                        </tr>
                      ))}
                      {this.state.monthly.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            Brak odbiorów w wybranym okresie.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                }
              />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card
                title="Zarejestrowane odbiory"
                ctTableFullWidth
                ctTableResponsive
                content={
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Data i godzina</th>
                        <th>Urządzenie</th>
                        <th>Ilość</th>
                        <th>Temperatura</th>
                        <th>Źródło</th>
                        <th>Notatka</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.collections.map((collection, index) => (
                        <tr
                          key={`${collection.device_id}-${collection.id}-${index}`}
                        >
                          <td>{this.formatDate(collection.started_at)}</td>
                          <td>
                            {collection.device_name || collection.device_id}
                          </td>
                          <td>{this.formatNumber(collection.amount, 1)}</td>
                          <td>
                            {collection.temperature === null
                              ? "—"
                              : `${this.formatNumber(
                                  collection.temperature,
                                  1
                                )} °C`}
                          </td>
                          <td>
                            {collection.source === "manual"
                              ? "Ręczny"
                              : collection.source === "device"
                                ? "Sterownik"
                                : "Wykryty"}
                          </td>
                          <td>{collection.note || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                }
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default CollectionStatistics;



// WEBPACK FOOTER //
// ./src/views/CollectionStatistics.jsx